import type {
  P360Profiles,
  TPartnerStatus,
} from 'legacy/lut/classes/models/partner360/IFormPartnerBasicInfo'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import type { IMappedGeoMappingItem } from 'partner360/components/filters'

export type CockpitPartnerProfileGroups = {
  grades: TPartnerStatus[]
  specializations: TPartnerStatus[]
  programs: TPartnerStatus[]
}

const getStatusKey = (status: TPartnerStatus) =>
  'Icon' in status ? `icon:${status.Icon}:${status.Title}` : `label:${status.Title}`

const getUniqueStatuses = (statuses: TPartnerStatus[]) => {
  const statusKeys = new Set<string>()

  return statuses.filter((status) => {
    const key = getStatusKey(status)

    if (statusKeys.has(key)) {
      return false
    }

    statusKeys.add(key)
    return true
  })
}

const isSelectedOffering = (offering: string, selectedOffering: ProgramTypes) =>
  selectedOffering === ProgramTypes.Overall || offering.trim() === selectedOffering.trim()

export const getCockpitPartnerProfileGroups = (
  profiles: P360Profiles[],
  selectedOffering: ProgramTypes
): CockpitPartnerProfileGroups => {
  const selectedProfiles = profiles.filter(({ Offering }) =>
    isSelectedOffering(Offering, selectedOffering)
  )

  return {
    grades: getUniqueStatuses(selectedProfiles.flatMap(({ Items }) => Items.P360Grades ?? [])),
    specializations: getUniqueStatuses(
      selectedProfiles.flatMap(({ Items }) => Items.P360Specializations ?? [])
    ),
    programs: getUniqueStatuses(selectedProfiles.flatMap(({ Items }) => Items.P360Programs ?? [])),
  }
}

export const getCockpitPartnerMemberships = (
  amplifyTrack: string,
  isPowerElite: boolean,
  isPowerEliteGlobal: boolean
) => [
  ...new Set(
    [amplifyTrack, isPowerEliteGlobal ? 'Elite Global' : isPowerElite ? 'Elite' : ''].filter(
      Boolean
    )
  ),
]

export const getCockpitPartnerMarketShortName = (
  geoMappingItems: IMappedGeoMappingItem[],
  geoMarket: string | null
) => {
  const market = geoMarket?.trim().toLowerCase()

  if (!market) {
    return ''
  }

  return (
    geoMappingItems.find(
      ({ itemId, name }) =>
        itemId.trim().toLowerCase() === market || name.trim().toLowerCase() === market
    )?.shortName ?? ''
  )
}

export const getCockpitPartnerRegionLabel = (geoMarket: string | null, country: string) =>
  [geoMarket, country].filter(Boolean).join(', ')
